import React, { useState } from "react";

import { FilterIcon, ListIcon, TreeIcon, InfoSmIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import { IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import * as Dropdown from "ds/Dropdown";
import * as Radio from "ds/Radio";
import Toggle from "ds/Toggle";

import * as S from "./NavBar.styles";

export interface NavBarProps {
  setShowEnvs: (value: { active: boolean; inactive: boolean }) => void;
  setTitleFilter: (value: string) => void;
  setTreeLayout: (value: boolean) => void;
  showEnvs: { active: boolean; inactive: boolean };
  sortType: "title" | "type" | "created_at" | "deployed_at";
  titleFilter: string;
  toggleSort: (
    e: React.MouseEvent | undefined,
    type: "title" | "type" | "created_at" | "deployed_at"
  ) => void;
  treeLayout: boolean;
}

const NavBar = ({
  setShowEnvs,
  setTitleFilter,
  setTreeLayout,
  showEnvs,
  sortType,
  titleFilter,
  toggleSort,
  treeLayout
}: NavBarProps) => {
  const [isDrodownOpen, setIsDropdownOpen] = useState(false);
  const intl = useIntl();

  return (
    <S.Wrapper>
      <S.Search
        placeholder={intl.formatMessage({
          id: "environmentTree.navbar.search",
          defaultMessage: "Search"
        })}
        onChange={value => setTitleFilter(value)}
        value={titleFilter}
        closedByDefault
        id="project-environment-search"
      />

      <Dropdown.Root
        open={isDrodownOpen}
        onOpenChange={setIsDropdownOpen}
        id="environmentTree.navbar.filter.label"
      >
        <Dropdown.Trigger>
          <S.IconFilterWrapperContainer>
            <S.IconFilterWrapper aria-label="filter-environment-tree">
              <FilterIcon />
            </S.IconFilterWrapper>
          </S.IconFilterWrapperContainer>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end">
            <S.FragmentLabel isMainLabel={true}>
              <S.FilterDropdownLabel>
                {intl.formatMessage({
                  id: "environmentTree.navbar.filter.label",
                  defaultMessage: "Filter by"
                })}
                <S.CloseWindow onClick={() => setIsDropdownOpen(false)} />
              </S.FilterDropdownLabel>
            </S.FragmentLabel>
            <Dropdown.CheckboxItem
              checked={showEnvs?.inactive}
              onCheckedChanged={() =>
                setShowEnvs({ ...showEnvs, inactive: !showEnvs.inactive })
              }
              id="toggle-inactive"
              disabled={false}
            >
              {intl.formatMessage({
                id: "environmentTree.navbar.filter.inactive",
                defaultMessage: "Inactive"
              })}
            </Dropdown.CheckboxItem>

            <Dropdown.CheckboxItem
              checked={showEnvs.active}
              onCheckedChanged={() =>
                setShowEnvs({ ...showEnvs, active: !showEnvs.active })
              }
              id="toggle-active"
              disabled={false}
            >
              {intl.formatMessage({
                id: "environmentTree.navbar.filter.active",
                defaultMessage: "Active"
              })}
            </Dropdown.CheckboxItem>
            <Dropdown.Separator />
            <S.FragmentLabel>
              <S.SearchByDropdownLabel>
                {intl.formatMessage({
                  id: "environmentTree.navbar.filter.sort",
                  defaultMessage: "Sort by"
                })}
                {treeLayout && (
                  <Dialog
                    align="center"
                    trigger={
                      <IconControl aria-label="info-trigger-sort">
                        <InfoSmIcon />
                      </IconControl>
                    }
                    analyticId="environmentTree.navbar.filter.sort_available"
                    description={intl.formatMessage({
                      id: "environmentTree.navbar.filter.sort_available",
                      defaultMessage:
                        "Environments can only be sorted in the list view."
                    })}
                  />
                )}
              </S.SearchByDropdownLabel>
            </S.FragmentLabel>

            <Radio.Group
              value={sortType}
              onValueChange={(value: typeof sortType) =>
                toggleSort(undefined, value)
              }
            >
              {["title", "created_at"].map(elt => (
                <S.RadioItem
                  key={`sort-by-${elt}`}
                  id={`sort-by-${elt}`}
                  value={elt}
                  disabled={treeLayout}
                >
                  {intl.formatMessage({
                    id: `environmentTree.navbar.sort.${elt}`
                  })}
                </S.RadioItem>
              ))}
            </Radio.Group>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <S.ToggleWrapper>
        <Toggle
          onPressedChange={() => {
            setTreeLayout(false);
          }}
          layout="tree"
          toggleType="icon"
          pressed={treeLayout}
          text={<TreeIcon color="var(--mode-vector-neutral-default)" />}
        />
        <Toggle
          layout="list"
          onPressedChange={() => {
            setTreeLayout(true);
          }}
          toggleType="icon"
          pressed={!treeLayout}
          text={<ListIcon color="var(--mode-vector-neutral-default)" />}
        />
      </S.ToggleWrapper>
    </S.Wrapper>
  );
};

export default NavBar;
